import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useEncounter } from '../contexts/EncounterContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '../components/ui/resizable';
import {
  Mic, Send, Loader2, FileText, Sparkles, ChevronRight, Check,
  AlertCircle, Scissors, Info, ChevronDown, Lock, Flag, Edit3
} from 'lucide-react';
import { toast } from 'sonner';
import VoiceControls from '../components/voice/VoiceControls';
import EncounterLeftPanel from '../components/encounter/EncounterLeftPanel';
import StagingRail from '../components/encounter/StagingRail';
import { QualityChipStrip } from '../components/quality/QualityChipStrip';
import { QualityChipStripDrawer } from '../components/quality/QualityChipStripDrawer';

export default function LiveVisit() {
  const { encounterId } = useParams();
  const navigate = useNavigate();
  const { activeEncounter, transcriptBuffer, startEncounter, updateEncounter, addTranscriptChunk } = useEncounter();
  const [patient, setPatient] = useState(null);
  const [pastEncounters, setPastEncounters] = useState([]);
  const [transcriptInput, setTranscriptInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiProgress, setAiProgress] = useState('');
  const [centerTab, setCenterTab] = useState('transcript');
  const [noteEdits, setNoteEdits] = useState({});
  const [encounter, setEncounter] = useState(null);
  const [finalizing, setFinalizing] = useState(false);
  const [procFieldEdits, setProcFieldEdits] = useState({});
  const [expandedMed, setExpandedMed] = useState(null);
  const [medEdits, setMedEdits] = useState({});
  const [codingExpanded, setCodingExpanded] = useState(false);
  const [prepPacket, setPrepPacket] = useState(null);
  const [prepCollapsed, setPrepCollapsed] = useState(false);
  const [noteStyle, setNoteStyle] = useState(() => localStorage.getItem('hfc_note_style') || 'standard');
  const [qualityOverview, setQualityOverview] = useState(null);
  const [qualityLoading, setQualityLoading] = useState(false);
  const [holdLoading, setHoldLoading] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [liveDrawerChip, setLiveDrawerChip] = useState(null);
  // Transcription state
  const [interimText, setInterimText] = useState('');
  const [interimSpeaker, setInterimSpeaker] = useState(null);
  const [transcriptViewMode, setTranscriptViewMode] = useState('diarized'); // 'diarized' | 'raw'
  const [diarizedChunks, setDiarizedChunks] = useState([]); // [{text, speaker, timestamp}]
  const transcriptBottomRef = React.useRef(null);

  const fetchEncounter = useCallback(async () => {
    try {
      const res = await api.get(`/encounters/${encounterId}`);
      const enc = res.data.encounter;
      if (enc.status === 'finalized') {
        navigate(`/note/${encounterId}`, { replace: true });
        return;
      }
      setEncounter(enc);
      startEncounter(enc);
      // Auto-enable manual mode + switch to note tab if started from chart in manual mode
      if (enc.note_mode === 'manual' || enc.source === 'chart_direct') {
        setCenterTab('note');
        if (enc.note_mode === 'manual') {
          setManualMode(true);
          // Pre-open all sections in edit mode
          setNoteEdits({ _editing_hpi: true, _editing_ros: true, _editing_exam: true, _editing_assessment_plan: true });
        }
      }
      const [patRes, pastRes] = await Promise.all([
        api.get(`/patients/${enc.patient_id}`),
        api.get(`/encounters/patient/${enc.patient_id}`),
      ]);
      setPatient(patRes.data.patient);
      setPastEncounters((pastRes.data.encounters || []).filter(e => e.id !== encounterId).slice(0, 3));
      if (enc.appointment_id) {
        try {
          const prepRes = await api.get(`/pre-visit/packet/${enc.appointment_id}`);
          setPrepPacket(prepRes.data.packet);
        } catch {}
      }
    } catch {
      toast.error('Failed to load encounter');
    }
  }, [encounterId, startEncounter, navigate]);

  useEffect(() => { fetchEncounter(); }, [fetchEncounter]);

  // Quality overview — load after encounter is set
  const fetchQuality = useCallback(async () => {
    if (!encounterId) return;
    setQualityLoading(true);
    try {
      const res = await api.get(`/encounters/${encounterId}/quality-overview?debug=true`);
      setQualityOverview(res.data);
    } catch {
      // Quality failure should never break the visit
    } finally {
      setQualityLoading(false);
    }
  }, [encounterId]);

  useEffect(() => { if (encounter) fetchQuality(); }, [encounter, fetchQuality]);

  useEffect(() => {
    api.get('/note-styles').then(res => {
      const s = res.data.current || 'standard';
      setNoteStyle(s);
      localStorage.setItem('hfc_note_style', s);
    }).catch(() => {});
  }, []);

  const sendTranscript = async () => {
    if (!transcriptInput.trim()) return;
    try {
      const res = await api.post(`/encounters/${encounterId}/transcript`, { text: transcriptInput, source: 'typed' });
      addTranscriptChunk({ text: transcriptInput, sequence: res.data.total_chunks - 1, source: 'typed' });
      setTranscriptInput('');
    } catch { toast.error('Failed to save transcript'); }
  };

  const runPipeline = async () => {
    setAiLoading(true);
    setAiProgress('Generating clinical note...');
    try {
      const res = await api.post('/ai/fast-pass', { encounter_id: encounterId });
      updateEncounter(res.data.encounter);
      setEncounter(res.data.encounter);
      const elapsed = res.data.fast_pass_elapsed_ms;
      toast.success(elapsed ? `Note ready (${(elapsed / 1000).toFixed(1)}s)` : 'Note ready');
      setCenterTab(res.data.encounter?.procedure_note?.detected ? 'procedure' : 'note');
      setAiLoading(false);
      setAiProgress('');
      try {
        await api.post('/ai/deep-stages', { encounter_id: encounterId });
        setTimeout(async () => {
          try {
            const updated = await api.get(`/encounters/${encounterId}`);
            if (updated.data.encounter?.deep_stages_completed) {
              setEncounter(updated.data.encounter);
              updateEncounter(updated.data.encounter);
            }
          } catch {}
        }, 8000);
      } catch {}
    } catch (err) {
      try {
        setAiProgress('Running full pipeline...');
        const res = await api.post('/ai/run-pipeline', { encounter_id: encounterId });
        updateEncounter(res.data.encounter);
        setEncounter(res.data.encounter);
        toast.success('AI pipeline complete');
        setCenterTab('note');
      } catch (err2) { toast.error(err2.response?.data?.detail || 'AI pipeline failed'); }
      setAiLoading(false);
      setAiProgress('');
    }
  };

  const saveNote = async (section, text) => {
    try {
      await api.put(`/encounters/${encounterId}/note`, { section, text, version: 1 });
      setNoteEdits(prev => ({ ...prev, [section]: text }));
    } catch { toast.error('Failed to save note'); }
  };

  const handleVoiceCommand = (cmd) => {
    if (cmd.action === 'navigate_chart' && patient) navigate(`/chart/${patient.id}`);
    else if (cmd.action === 'open_quick_note') setCenterTab('note');
  };

  // Handle incoming transcript chunk (final result from Deepgram/browser)
  const handleTranscriptChunk = useCallback((chunk) => {
    addTranscriptChunk(chunk);
    // Add to diarized view
    setDiarizedChunks(prev => {
      const last = prev[prev.length - 1];
      // Merge consecutive chunks from same speaker
      const spk = chunk.speaker ?? null;
      if (last && last.speaker === spk && (Date.now() - last._ts) < 8000) {
        const updated = [...prev];
        updated[updated.length - 1] = {
          ...last,
          text: last.text + ' ' + chunk.text,
          _ts: Date.now(),
        };
        return updated;
      }
      return [...prev, { text: chunk.text, speaker: spk, timestamp: new Date().toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}), _ts: Date.now() }];
    });
  }, [addTranscriptChunk]);

  // Handle interim text (in-progress, not yet final)
  const handleInterim = useCallback((text, speaker) => {
    setInterimText(text);
    setInterimSpeaker(speaker);
  }, []);

  // Auto-scroll transcript to bottom
  useEffect(() => {
    if (transcriptBottomRef.current) {
      transcriptBottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [diarizedChunks, interimText]);

  const handleHold = async () => {
    setHoldLoading(true);
    try {
      await api.post(`/encounters/${encounterId}/hold`, { reason: 'Provider flagged for follow-up' });
      toast.success('Encounter flagged — stays in My Day until finalized');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to flag');
    } finally {
      setHoldLoading(false);
    }
  };

  const enc = encounter || activeEncounter;
  const transcript = transcriptBuffer.length > 0 ? transcriptBuffer : (enc?.transcript?.chunks || []);
  const note = enc?.note || {};
  const outputs = enc?.encounter_outputs || {};
  const detections = enc?.chart_intelligence_detections || [];

  return (
    <div className="flex flex-col h-full animate-fadeIn" data-testid="clinical-workspace">
      <VoiceControls
        encounterId={encounterId}
        onTranscriptChunk={handleTranscriptChunk}
        onInterim={handleInterim}
        onCommand={handleVoiceCommand}
      />

      {/* Patient Header */}
      {patient && (
        <div className="px-4 py-2 border-b bg-white/80 backdrop-blur-xl flex items-center gap-3 shrink-0">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary">
            {patient.name?.first?.[0]}{patient.name?.last?.[0]}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">{patient.name?.first} {patient.name?.last}</span>
              <Badge variant="outline" className="text-[10px] font-mono">{patient.mrn}</Badge>
              <span className="text-[10px] text-muted-foreground tabular-nums">{patient.dob} - {patient.sex}</span>
            </div>
          </div>
          {(patient.allergies || []).length > 0 && (
            <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200 gap-0.5 shrink-0">
              <AlertCircle className="w-2.5 h-2.5" /> {patient.allergies.map(a => a.name).join(', ')}
            </Badge>
          )}
          <Button variant="ghost" size="sm" className="h-7 text-xs shrink-0" onClick={() => navigate(`/chart/${patient.id}`)}>
            Chart <ChevronRight className="w-3 h-3" />
          </Button>
          <Button variant="ghost" size="sm"
            className={`h-7 text-xs shrink-0 gap-1 ${enc?.needs_followup ? 'text-amber-600 bg-amber-50' : 'text-muted-foreground'}`}
            onClick={handleHold} disabled={holdLoading || enc?.needs_followup}
            data-testid="live-visit-flag-followup">
            {holdLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Flag className="w-3 h-3" />}
            {enc?.needs_followup ? 'Flagged' : 'Flag Follow-Up'}
          </Button>
        </div>
      )}

      {/* 3-Panel Resizable Layout */}
      <ResizablePanelGroup direction="horizontal" className="flex-1" data-testid="live-visit-resizable-layout">
        {/* LEFT */}
        <ResizablePanel defaultSize={22} minSize={15} maxSize={35}>
          <EncounterLeftPanel patient={patient} pastEncounters={pastEncounters}
            prepPacket={prepPacket} prepCollapsed={prepCollapsed} setPrepCollapsed={setPrepCollapsed} navigate={navigate}
            encounter={encounter} />
        </ResizablePanel>

        <ResizableHandle withHandle />

        {/* CENTER: Transcript + Note + Procedure */}
        <ResizablePanel defaultSize={50} minSize={30}>
          <div className="flex flex-col h-full">
            {/* Quality Chip Strip — real-time, polls every 10s */}
            {enc?.patient_id && (
              <div className="px-4 py-1.5 border-b bg-slate-50/60 flex items-center gap-2 shrink-0 min-h-[32px]"
                data-testid="live-visit-quality-chips">
                <QualityChipStrip
                  patientId={enc.patient_id}
                  encounterId={encounterId}
                  context="live_visit"
                  density="compact"
                  onChipClick={(chip) => setLiveDrawerChip(chip)}
                  className="flex-1"
                />
              </div>
            )}
            <div className="border-b px-4 py-2 flex items-center gap-2 shrink-0">
              <Tabs value={centerTab} onValueChange={setCenterTab} className="flex-1">
                <TabsList className="h-8">
                  <TabsTrigger value="transcript" className="text-xs" data-testid="live-visit-transcript-tab">
                    <Mic className="w-3 h-3 mr-1" /> Transcript
                  </TabsTrigger>
                  <TabsTrigger value="note" className="text-xs" data-testid="live-visit-soap-note-tab">
                    <FileText className="w-3 h-3 mr-1" /> SOAP Note
                  </TabsTrigger>
                  {enc?.procedure_note?.detected && (
                    <TabsTrigger value="procedure" className="text-xs" data-testid="live-visit-procedure-note-tab">
                      <Scissors className="w-3 h-3 mr-1" /> Procedure Note
                    </TabsTrigger>
                  )}
                </TabsList>
              </Tabs>
              <div className="flex items-center gap-1.5">
                <select className="h-7 text-[10px] border rounded-md px-1.5 bg-background text-muted-foreground"
                  value={noteStyle}
                  onChange={async (e) => {
                    const s = e.target.value;
                    setNoteStyle(s);
                    localStorage.setItem('hfc_note_style', s);
                    try { await api.put('/note-styles', { style: s }); } catch {}
                  }}
                  data-testid="note-style-selector">
                  <option value="detailed">Detailed</option>
                  <option value="standard">Standard</option>
                  <option value="concise">Concise</option>
                </select>
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={runPipeline}
                  disabled={aiLoading || transcript.length === 0}>
                  {aiLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  {aiLoading ? (aiProgress || 'Processing...') : 'Generate Note'}
                </Button>
                {/* Manual Entry toggle */}
                <Button size="sm" variant={manualMode ? 'default' : 'ghost'}
                  className={`h-7 text-xs gap-1 ${manualMode ? '' : 'text-muted-foreground'}`}
                  onClick={() => {
                    const next = !manualMode;
                    setManualMode(next);
                    if (next) {
                      setCenterTab('note');
                      setNoteEdits(prev => ({ ...prev, _editing_hpi: true, _editing_ros: true, _editing_exam: true, _editing_assessment_plan: true }));
                    } else {
                      setNoteEdits(prev => {
                        const n = { ...prev };
                        delete n._editing_hpi; delete n._editing_ros; delete n._editing_exam; delete n._editing_assessment_plan;
                        return n;
                      });
                    }
                  }}
                  data-testid="manual-entry-toggle">
                  <Edit3 className="w-3 h-3" />
                  {manualMode ? 'Manual On' : 'Manual'}
                </Button>
              </div>
            </div>

            <div className="flex-1 overflow-auto">
              {/* Transcript Tab */}
              {centerTab === 'transcript' && (
                <div className="flex flex-col h-full">
                  {/* View toggle: Diarized | Raw */}
                  <div className="px-3 pt-2 pb-1 flex items-center gap-2 border-b shrink-0">
                    <div className="flex rounded-md overflow-hidden border text-[10px] font-medium">
                      <button
                        className={`px-2.5 py-1 transition-colors ${transcriptViewMode === 'diarized' ? 'bg-foreground text-background' : 'bg-card text-muted-foreground hover:text-foreground'}`}
                        onClick={() => setTranscriptViewMode('diarized')}
                        data-testid="transcript-view-diarized">
                        Diarized
                      </button>
                      <button
                        className={`px-2.5 py-1 transition-colors ${transcriptViewMode === 'raw' ? 'bg-foreground text-background' : 'bg-card text-muted-foreground hover:text-foreground'}`}
                        onClick={() => setTranscriptViewMode('raw')}
                        data-testid="transcript-view-raw">
                        Raw
                      </button>
                    </div>
                    <span className="text-[10px] text-muted-foreground ml-auto">
                      {transcript.length} chunk{transcript.length !== 1 ? 's' : ''}
                    </span>
                  </div>

                  <ScrollArea className="flex-1 p-4">
                    {transcriptViewMode === 'diarized' ? (
                      /* ── Diarized View: speaker-labeled conversation ── */
                      <div className="space-y-3">
                        {diarizedChunks.length === 0 && transcript.length === 0 && (
                          <div className="text-center py-12">
                            <Mic className="w-8 h-8 mx-auto mb-2 text-muted-foreground/30" />
                            <p className="text-sm text-muted-foreground">Click "Start Recording" to begin transcription</p>
                            <p className="text-[11px] text-muted-foreground/60 mt-1">Deepgram Medical STT with speaker separation</p>
                          </div>
                        )}
                        {/* Populate from diarizedChunks (live recording) or fall back to raw chunks */}
                        {(diarizedChunks.length > 0 ? diarizedChunks : transcript.map((c, i) => ({
                          text: c.text, speaker: c.speaker ?? null, timestamp: '', _ts: i
                        }))).map((chunk, i) => {
                          const spk = chunk.speaker;
                          const isPhysician = spk === 0 || spk === null;
                          const label = spk === null ? null : isPhysician ? 'Physician' : 'Patient';
                          const labelColor = isPhysician ? 'text-blue-600 bg-blue-50 border-blue-200' : 'text-emerald-600 bg-emerald-50 border-emerald-200';
                          return (
                            <div key={i} className="flex gap-2.5 animate-fadeIn" data-testid={`diarized-chunk-${i}`}>
                              <div className="flex flex-col items-end gap-0.5 shrink-0 w-16">
                                {label && (
                                  <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded border ${labelColor}`}>{label}</span>
                                )}
                                {chunk.timestamp && (
                                  <span className="text-[9px] text-muted-foreground/50 font-mono">{chunk.timestamp}</span>
                                )}
                              </div>
                              <div className={`flex-1 rounded-lg px-3 py-2 text-sm leading-relaxed ${
                                isPhysician ? 'bg-blue-50/40' : 'bg-emerald-50/40'
                              }`}>
                                {chunk.text}
                              </div>
                            </div>
                          );
                        })}
                        {/* Interim (in-progress) text */}
                        {interimText && (
                          <div className="flex gap-2.5 animate-fadeIn opacity-60">
                            <div className="flex flex-col items-end gap-0.5 shrink-0 w-16">
                              {interimSpeaker !== null && (
                                <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded border ${interimSpeaker === 0 ? 'text-blue-600 bg-blue-50 border-blue-200' : 'text-emerald-600 bg-emerald-50 border-emerald-200'}`}>
                                  {interimSpeaker === 0 ? 'Physician' : 'Patient'}
                                </span>
                              )}
                            </div>
                            <div className="flex-1 rounded-lg px-3 py-2 text-sm leading-relaxed bg-muted/30 border border-dashed border-muted-foreground/20 italic text-muted-foreground">
                              {interimText}
                              <span className="inline-block w-1.5 h-3.5 bg-muted-foreground/40 ml-1 animate-pulse" />
                            </div>
                          </div>
                        )}
                        <div ref={transcriptBottomRef} />
                      </div>
                    ) : (
                      /* ── Raw View: sequential numbered chunks ── */
                      <div className="space-y-2">
                        {transcript.length === 0 ? (
                          <div className="text-center py-12">
                            <Mic className="w-8 h-8 mx-auto mb-2 text-muted-foreground/30" />
                            <p className="text-sm text-muted-foreground">Use voice controls above or type transcript below</p>
                          </div>
                        ) : transcript.map((chunk, i) => (
                          <div key={i} className="flex gap-2 animate-fadeIn">
                            <span className="text-[10px] text-muted-foreground tabular-nums font-mono mt-1 w-6 shrink-0">{i + 1}</span>
                            <p className="text-sm leading-relaxed">{chunk.text}</p>
                          </div>
                        ))}
                        {interimText && (
                          <div className="flex gap-2 opacity-60">
                            <span className="text-[10px] text-muted-foreground tabular-nums font-mono mt-1 w-6 shrink-0">…</span>
                            <p className="text-sm leading-relaxed italic text-muted-foreground">{interimText}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </ScrollArea>

                  <div className="border-t p-3 space-y-2 shrink-0">
                    <div className="flex gap-2">
                      <textarea value={transcriptInput} onChange={e => setTranscriptInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendTranscript(); } }}
                        placeholder="Type or paste transcript content..."
                        className="flex-1 resize-none border rounded-md px-3 py-2 text-sm bg-background min-h-[40px] max-h-[100px]" rows={1} />
                      <Button size="sm" onClick={sendTranscript} disabled={!transcriptInput.trim()} className="h-10 px-3"
                        title="Send transcript chunk">
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                    {/* Script tools — always visible so imported transcripts can be replaced */}
                    <div className="flex items-center gap-2">
                      <span className="text-[9px] text-muted-foreground">Script tools:</span>
                      <Button variant="outline" size="sm" className="h-6 text-[9px] px-2" data-testid="transcript-replace-btn"
                        onClick={async () => {
                          if (!transcriptInput.trim()) { toast.error('Enter or paste transcript text first'); return; }
                          const confirmed = transcript.length > 0
                            ? window.confirm('Replace entire transcript? This will reset all generated outputs.')
                            : true;
                          if (!confirmed) return;
                          try {
                            await api.put(`/encounters/${encounterId}/transcript/replace`, { text: transcriptInput });
                            setTranscriptInput('');
                            setDiarizedChunks([]);
                            toast.success('Transcript saved — click Generate Note to create the SOAP note');
                            fetchEncounter();
                          } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                        }}>
                        {transcript.length > 0 ? 'Replace Script' : 'Import Transcript'}
                      </Button>
                        <Button variant="outline" size="sm" className="h-6 text-[9px] px-2 text-red-600" data-testid="transcript-clear-btn"
                          onClick={async () => {
                            if (!window.confirm('Clear transcript and all outputs?')) return;
                            try {
                              await api.delete(`/encounters/${encounterId}/transcript`);
                              setDiarizedChunks([]);
                              toast.success('Transcript cleared');
                              fetchEncounter();
                            } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                          }}>
                          Clear
                        </Button>
                      </div>
                  </div>
                </div>
              )}

              {/* SOAP Note Tab */}
              {centerTab === 'note' && (
                <ScrollArea className="h-full">
                  <div className="p-4 space-y-1">
                    {/* Manual mode banner */}
                    {manualMode && (
                      <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-violet-50 border border-violet-200">
                        <Edit3 className="w-3.5 h-3.5 text-violet-600" />
                        <span className="text-xs text-violet-700 font-medium flex-1">Manual Entry — type directly into each section below</span>
                        <button onClick={() => setCenterTab('transcript')} className="text-[10px] text-violet-500 hover:text-violet-700">
                          Switch to Transcript
                        </button>
                      </div>
                    )}
                    <div className="prose prose-sm max-w-none" data-testid="note-document">
                      {['hpi', 'ros', 'exam'].map(section => {
                        const isEditing = manualMode || noteEdits[`_editing_${section}`];
                        const text = noteEdits[section] !== undefined ? noteEdits[section] : (note[section]?.text || '');
                        return (
                          <div key={section} className="mb-4" data-testid={`note-section-${section}`}>
                            <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1 select-none">{section}</h4>
                            {isEditing ? (
                              <textarea className="w-full border rounded-md px-3 py-2 text-sm bg-background resize-none focus:ring-1 focus:ring-primary/30"
                                style={{ minHeight: '60px', height: 'auto' }} value={text}
                                onChange={e => setNoteEdits(prev => ({ ...prev, [section]: e.target.value }))}
                                onBlur={e => {
                                  saveNote(section, e.target.value);
                                  if (!manualMode) setNoteEdits(prev => { const n = {...prev}; delete n[`_editing_${section}`]; return n; });
                                }}
                                ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }} />
                            ) : (
                              <div className="relative group cursor-text rounded-md px-3 py-2 hover:bg-accent/30 transition-colors min-h-[24px]"
                                onClick={() => setNoteEdits(prev => ({ ...prev, [`_editing_${section}`]: true }))}>
                                <p className="text-sm leading-relaxed whitespace-pre-wrap">
                                  {text || <span className="text-muted-foreground/40 italic">Click to add {section.toUpperCase()}...</span>}
                                </p>
                                <Edit3 className="absolute top-2 right-2 w-3 h-3 text-muted-foreground/40 opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {/* Lab Review */}
                      {(note.lab_review || []).length > 0 && (
                        <div className="mb-4" data-testid="note-section-labs">
                          <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Lab Review</h4>
                          <div className="space-y-1">
                            {note.lab_review.map((lab, i) => (
                              <div key={i} className="text-sm flex items-baseline gap-1">
                                <span className="font-medium">{lab.test}:</span>
                                <span>{lab.value}</span>
                                {lab.interpretation && <span className="text-muted-foreground">— {lab.interpretation}</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Assessment & Plan */}
                      <div className="mb-4" data-testid="note-section-ap">
                        <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Assessment & Plan</h4>
                        {(note.problem_oriented_ap || []).length > 0 ? (
                          <div className="space-y-3">
                            {note.problem_oriented_ap.map((problem, idx) => (
                              <div key={idx} className="pl-1 py-1.5">
                                <p className="text-sm font-bold">{problem.problem_number || idx + 1}. {problem.problem_name}</p>
                                {Array.isArray(problem.plan_items) ? (
                                  <ul className="mt-1 space-y-0.5 pl-4">
                                    {problem.plan_items.map((item, j) => (
                                      <li key={j} className="text-sm leading-relaxed list-disc">{item}</li>
                                    ))}
                                  </ul>
                                ) : problem.plan ? (
                                  <p className="text-sm leading-relaxed mt-1 pl-4">{problem.plan}</p>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        ) : (
                          (() => {
                            const apText = noteEdits['assessment_plan'] !== undefined ? noteEdits['assessment_plan']
                              : [note.assessment?.text, note.plan?.text].filter(Boolean).join('\n\n') || '';
                            const isEditingAP = manualMode || noteEdits['_editing_assessment_plan'];
                            return isEditingAP ? (
                              <textarea className="w-full border rounded-md px-3 py-2 text-sm bg-background resize-none focus:ring-1 focus:ring-primary/30"
                                style={{ minHeight: '80px', height: 'auto' }} value={apText}
                                onChange={e => setNoteEdits(prev => ({ ...prev, assessment_plan: e.target.value }))}
                                onBlur={e => {
                                  // Save A&P as both assessment and plan sections
                                  const txt = e.target.value;
                                  api.put(`/encounters/${encounterId}/note`, { section: 'assessment', text: txt, version: 1 }).catch(() => {});
                                  api.put(`/encounters/${encounterId}/note`, { section: 'plan', text: txt, version: 1 }).catch(() => {});
                                  if (!manualMode) setNoteEdits(prev => { const n = {...prev}; delete n['_editing_assessment_plan']; return n; });
                                }}
                                ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }} />
                            ) : (
                              <div className="text-sm leading-relaxed whitespace-pre-wrap cursor-text rounded-md px-3 py-2 hover:bg-accent/30 transition-colors min-h-[24px]"
                                onClick={() => setNoteEdits(prev => ({ ...prev, '_editing_assessment_plan': true }))}>
                                {apText || <span className="text-muted-foreground/40 italic">Assessment & Plan will appear after generating note...</span>}
                              </div>
                            );
                          })()
                        )}
                      </div>

                      {/* Shared Decisions */}
                      {(note.shared_decisions || []).length > 0 && (
                        <div className="mb-4" data-testid="note-section-decisions">
                          <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Shared Decision-Making</h4>
                          <ul className="space-y-0.5 pl-4">
                            {note.shared_decisions.map((d, i) => <li key={i} className="text-sm leading-relaxed list-disc">{d}</li>)}
                          </ul>
                        </div>
                      )}

                      {/* Follow-up */}
                      {note.follow_up_interval && (
                        <div className="mb-4" data-testid="note-section-followup">
                          <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Follow-Up</h4>
                          <p className="text-sm">{note.follow_up_interval}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </ScrollArea>
              )}

              {/* Procedure Note Tab */}
              {centerTab === 'procedure' && enc?.procedure_note?.detected && (
                <ScrollArea className="h-full">
                  <div className="p-4 space-y-3" data-testid="procedure-note-tab">
                    <div className="flex items-center justify-between pb-2 border-b">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px] gap-1 bg-violet-50 text-violet-700 border-violet-200">
                          <Scissors className="w-2.5 h-2.5" /> {enc.procedure_note.template_name || 'Procedure'}
                        </Badge>
                        <Badge variant="outline" className="text-[10px] gap-1 bg-sky-50 text-sky-600 border-sky-200">
                          <Info className="w-2.5 h-2.5" /> Template Mode
                        </Badge>
                      </div>
                      <span className="text-[10px] text-muted-foreground italic">Template customization coming soon</span>
                    </div>
                    {enc.procedure_note.rendered_text && (
                      <div className="bg-muted/30 rounded-lg p-4 text-sm leading-relaxed whitespace-pre-wrap font-mono" data-testid="procedure-note-rendered">
                        {enc.procedure_note.rendered_text}
                      </div>
                    )}
                    <div className="space-y-2 pt-2 border-t" data-testid="procedure-note-fields">
                      <h4 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Procedure Details</h4>
                      {(enc.procedure_note.field_definitions || []).map(field => {
                        const val = procFieldEdits[field.key] !== undefined ? procFieldEdits[field.key] : (enc.procedure_note.field_values || {})[field.key] || '';
                        const isEmpty = !val;
                        return (
                          <div key={field.key} className="flex items-start gap-2" data-testid={`proc-field-${field.key}`}>
                            <label className={`text-[10px] font-medium w-28 shrink-0 pt-2 ${field.required && isEmpty ? 'text-amber-600' : 'text-muted-foreground'}`}>
                              {field.label}{field.required && isEmpty ? ' *' : ''}
                            </label>
                            {field.type === 'select' ? (
                              <select className="flex-1 border rounded-md px-2 py-1.5 text-xs bg-background" value={val}
                                onChange={e => setProcFieldEdits(prev => ({ ...prev, [field.key]: e.target.value }))}
                                onBlur={() => { if (procFieldEdits[field.key] !== undefined) { api.put(`/encounters/${encounterId}/procedure-note`, { field_values: { [field.key]: procFieldEdits[field.key] } }).then(res => setEncounter(prev => ({...prev, procedure_note: res.data.procedure_note}))).catch(() => {}); } }}>
                                <option value="">Select...</option>
                                {(field.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                              </select>
                            ) : (
                              <input type="text" className={`flex-1 border rounded-md px-2 py-1.5 text-xs bg-background ${field.required && isEmpty ? 'border-amber-300' : ''}`}
                                placeholder={field.default || `Enter ${field.label.toLowerCase()}...`} value={val}
                                onChange={e => setProcFieldEdits(prev => ({ ...prev, [field.key]: e.target.value }))}
                                onBlur={() => { if (procFieldEdits[field.key] !== undefined) { api.put(`/encounters/${encounterId}/procedure-note`, { field_values: { [field.key]: procFieldEdits[field.key] } }).then(res => setEncounter(prev => ({...prev, procedure_note: res.data.procedure_note}))).catch(() => {}); } }} />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </ScrollArea>
              )}
            </div>
          </div>
        </ResizablePanel>

        <ResizableHandle withHandle />

        {/* RIGHT — Staging Rail */}
        <ResizablePanel defaultSize={28} minSize={18} maxSize={40}>
          <StagingRail
            enc={enc} outputs={outputs} encounterId={encounterId}
            fetchEncounter={fetchEncounter} setEncounter={setEncounter}
            qualityOverview={qualityOverview} qualityLoading={qualityLoading} onQualityRefresh={fetchQuality}
            expandedMed={expandedMed} setExpandedMed={setExpandedMed}
            medEdits={medEdits} setMedEdits={setMedEdits}
          />
        </ResizablePanel>
      </ResizablePanelGroup>

      {/* Quality chip drawer */}
      {liveDrawerChip && (
        <QualityChipStripDrawer
          chip={liveDrawerChip}
          encounterId={encounterId}
          onClose={() => setLiveDrawerChip(null)}
          onActionTaken={() => { setLiveDrawerChip(null); }}
        />
      )}
    </div>
  );
}
